/**
 * Thin fetch wrapper. Every call carries the session cookie; in development
 * Vite proxies /api and /auth to the API so the browser stays on one origin.
 */
const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}) as Record<string, unknown>);

  if (!response.ok) {
    const body = payload as { error?: string; code?: string };
    throw new ApiError(response.status, body.error ?? `Request failed (${response.status})`, body.code);
  }
  return payload as T;
}

/**
 * Which finished-job announcements this account wants.
 *
 * Two channels, because they cover different absences: email is the only one
 * that reaches someone who closed the tab — which is the normal way to use
 * this service — and a browser notification is the only one that arrives the
 * moment it happens. Two events, because wanting to hear about failures and
 * not about successes is a real preference.
 */
export interface NotificationPrefs {
  emailOnDone: boolean;
  emailOnFailure: boolean;
  browserOnDone: boolean;
  browserOnFailure: boolean;
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  subscriptionStatus: string;
  /** Credits available to spend. Always 0 on a deploy with billing switched off. */
  credits: number;
  plan: string | null;
  notifications: NotificationPrefs;
  createdAt: string;
}

export interface PricedSku {
  id: string;
  kind: 'pack' | 'plan';
  name: string;
  amountCents: number;
  currency: string;
  credits: number;
  blurb: string;
  highlight?: boolean;
  /** False when this deploy has no Stripe price wired to the SKU. */
  available: boolean;
  centsPerCredit: number;
}

/**
 * A worked example, measured by running the real planner server-side rather
 * than written into the marketing copy — so the page cannot promise pacing the
 * engine does not deliver.
 */
export interface ReferencePoint {
  chars: number;
  words: number;
  credits: number;
  durationMs: number;
  revisions: number;
  corrections: number;
}

export interface CatalogResponse {
  /** False on a self-hosted or local deploy: everything is free and the paywall is off. */
  enabled: boolean;
  charsPerCredit: number;
  maxCreditsPerJob: number;
  signupGrantCredits: number;
  skus: PricedSku[];
  credits: number | null;
  reference: ReferencePoint[];
  charsPerWord: number;
}

export interface BillingSummary {
  credits: number;
  plan: string | null;
  subscriptionStatus: string;
  currentPeriodEnd: string | null;
  hasBillingAccount: boolean;
  ledger: Array<{
    delta: number;
    balanceAfter: number;
    reason: string;
    createdAt: string;
  }>;
}

export type JobStatus = 'pending' | 'running' | 'paused' | 'done' | 'failed' | 'cancelled';

export interface CreateJobResponse {
  jobId: string;
  docId: string;
  docUrl: string;
  totalChars: number;
  started: boolean;
  queuePosition: number;
  estimatedMs: number;
  minDurationMs: number;
  /** Separate writing sessions — roughly how many revisions the doc will show. */
  bursts: number;
  /** Mistakes the plan will make and later go back to fix. */
  typos: number;
  /** Credits this job cost. Zero when billing is off. */
  creditsSpent: number;
  creditsRemaining: number;
}

export interface EstimateResponse {
  minDurationMs: number;
  bursts: number;
  typos: number;
  totalChars: number;
  maxDurationMs: number;
  /** What this text would cost to write, so the price is visible before submitting. */
  credits: number;
  creditsAvailable: number;
}

/**
 * Who runs the service, as the legal pages should name them. Served by the API
 * rather than baked into the bundle so a correction during Google's OAuth
 * review is an environment change rather than a redeploy.
 */
export interface LegalEntity {
  operator: string;
  contactEmail: string;
  /** Empty when the deploy has not set one — the page says so rather than inventing it. */
  jurisdiction: string;
  lastUpdated: string;
}

/** Deployment facts the browser needs before anyone signs in. */
export interface PublicConfig {
  legal: LegalEntity;
  /**
   * False until Google finishes verifying the OAuth app. While false, everyone
   * signing in meets the "Google hasn't verified this app" interstitial and has
   * to click through Advanced → Go to TurtleType (unsafe), so the sign-in page
   * says so first.
   */
  oauthVerified: boolean;
  /**
   * What the browser needs to open the Google Picker. Public values — the
   * Picker runs client-side and Google requires them there — served rather
   * than compiled in, so changing the API key does not mean rebuilding the
   * bundle inside the Docker image.
   *
   * `enabled` is false on a deploy with no Picker API key configured, and the
   * app falls back to the paste-a-link field it has always had.
   */
  picker: {
    enabled: boolean;
    clientId: string;
    apiKey: string;
    appId: string;
  };
  /**
   * Whether this deploy stops a gap as soon as it sees Google record the
   * revision, instead of waiting the planner's worst case out. False on a
   * deploy that has switched the behaviour off, in which case the composer
   * must not advertise it.
   */
  confirmsCheckpoints: boolean;
  /**
   * What this deploy can actually deliver. `email` is false with no mail
   * provider configured, in which case the settings panel says so rather than
   * offering a switch that does nothing — browser notifications need nothing
   * from the server and are unaffected.
   */
  notifications: { email: boolean };
}

export interface JobSnapshot {
  id: string;
  docId: string;
  docUrl: string | null;
  status: JobStatus;
  progressPct: number;
  totalChars: number;
  charsWritten: number;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export const api = {
  me: () => request<{ user: CurrentUser }>('/api/me'),

  createJob: (input: { text: string; durationMs?: number; docId?: string }) =>
    request<CreateJobResponse>('/api/jobs', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** How long this text would take at minimum, and how many bursts that is. */
  estimate: (input: { text: string }, signal?: AbortSignal) =>
    request<EstimateResponse>('/api/estimate', {
      method: 'POST',
      body: JSON.stringify(input),
      ...(signal ? { signal } : {}),
    }),

  getJob: (jobId: string) =>
    request<{ job: JobSnapshot; live: boolean; paused: boolean }>(`/api/jobs/${jobId}`),

  pauseJob: (jobId: string) => request<{ ok: true }>(`/api/jobs/${jobId}/pause`, { method: 'POST' }),

  resumeJob: (jobId: string) => request<{ ok: true }>(`/api/jobs/${jobId}/resume`, { method: 'POST' }),

  cancelJob: (jobId: string) => request<{ ok: true }>(`/api/jobs/${jobId}`, { method: 'DELETE' }),

  /**
   * Changes notification settings. Partial on purpose: the panel sends the one
   * switch that moved, so two open tabs cannot revert each other's changes.
   */
  updateNotifications: (patch: Partial<NotificationPrefs>) =>
    request<{ user: CurrentUser }>('/api/me/notifications', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  catalog: () => request<CatalogResponse>('/api/billing/catalog'),

  /** Operator identity and OAuth verification state. Readable without a session. */
  publicConfig: () => request<PublicConfig>('/api/public-config'),

  billingSummary: () => request<BillingSummary>('/api/billing/me'),

  /**
   * Starts a Stripe Checkout session. Returns the URL to send the browser to —
   * the redirect is left to the caller so the button can show a pending state
   * until the navigation actually happens.
   */
  checkout: (skuId: string) =>
    request<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ skuId }),
    }),

  /** Stripe's hosted portal: invoices, cards, cancellation. */
  billingPortal: () => request<{ url: string }>('/api/billing/portal', { method: 'POST' }),
};

export const streamUrl = (jobId: string): string => `${BASE}/api/jobs/${jobId}/stream`;
export const loginUrl = (next = '/app'): string =>
  `${BASE}/auth/google?next=${encodeURIComponent(next)}`;
